import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Modal, Button, Input, Select, UserSelect, Label } from "@/components/UI";
import { Plus, Trash2 } from "lucide-react";

const emptyRow = () => ({ title: "", assignee_id: "", priority: "medium", weight: "", start_date: "", deadline: "" });
const emptyForm = { name: "" };

export default function PhaseModal({ open, onClose, projectId, phase, meta, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [rows, setRows] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);
  const isEdit = !!phase;

  useEffect(() => {
    if (!open) return;
    setForm({ name: phase?.name || "" });
    setRows([emptyRow()]);
  }, [open, phase]);

  const updateRow = (idx, key, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };
  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const validRows = rows.filter((r) => r.title.trim() !== "");

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/phases/${phase.id}`, { name: form.name });
        toast.success("Tahapan diperbarui");
      } else {
        await api.post(`/projects/${projectId}/phases`, {
          name: form.name,
          tasks: validRows.map((r) => ({
            title: r.title.trim(),
            assignee_id: r.assignee_id || null,
            priority: r.priority || "medium",
            weight: r.weight === "" ? null : Number(r.weight),
            start_date: r.start_date || null,
            deadline: r.deadline || null,
          })),
        });
        toast.success(
          validRows.length > 0 ? `Tahapan dibuat dengan ${validRows.length} backlog` : "Tahapan dibuat"
        );
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal menyimpan tahapan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size={isEdit ? "md" : "xl"}
      title={isEdit ? "Ubah Tahapan" : "Tambah Tahapan"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving || !form.name.trim()} data-testid="phase-save">
            {saving ? "Menyimpan..." : isEdit ? "Simpan" : "Buat Tahapan"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <Label>Nama Tahapan</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ name: e.target.value })}
            placeholder="Contoh: Development, Testing, Deployment"
            data-testid="phase-name-input"
          />
        </div>

        {!isEdit && (
          <div className="space-y-2">
            <Label>Backlog di Tahapan Ini (opsional, bisa ditambah belakangan)</Label>
            <div className="overflow-x-auto rounded-xl border border-slate-800">
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="border-b border-slate-800 bg-slate-950/60 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2">Judul Task</th>
                    <th className="px-3 py-2">PIC</th>
                    <th className="px-3 py-2">Priority</th>
                    <th className="px-3 py-2">Bobot</th>
                    <th className="px-3 py-2">Mulai</th>
                    <th className="px-3 py-2">Deadline</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={idx} className="border-b border-slate-800/60 last:border-0" data-testid={`phase-task-row-${idx}`}>
                      <td className="px-3 py-2">
                        <Input
                          value={row.title}
                          onChange={(e) => updateRow(idx, "title", e.target.value)}
                          placeholder="Nama task"
                          data-testid={`phase-task-title-${idx}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <UserSelect users={meta?.users || []} value={row.assignee_id} onChange={(v) => updateRow(idx, "assignee_id", v)} placeholder="— Unassigned —" data-testid={`phase-task-assignee-${idx}`} />
                      </td>
                      <td className="px-3 py-2">
                        <Select value={row.priority} onChange={(e) => updateRow(idx, "priority", e.target.value)} data-testid={`phase-task-priority-${idx}`}>
                          {["low", "medium", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
                        </Select>
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          min={1}
                          max={200}
                          value={row.weight}
                          onChange={(e) => updateRow(idx, "weight", e.target.value)}
                          placeholder="pts"
                          className="w-20"
                          data-testid={`phase-task-weight-${idx}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input type="date" value={row.start_date} onChange={(e) => updateRow(idx, "start_date", e.target.value)} data-testid={`phase-task-start-${idx}`} />
                      </td>
                      <td className="px-3 py-2">
                        <Input type="date" value={row.deadline} onChange={(e) => updateRow(idx, "deadline", e.target.value)} data-testid={`phase-task-deadline-${idx}`} />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeRow(idx)}
                          className="rounded-lg p-1.5 text-slate-500 hover:bg-rose-500/10 hover:text-rose-400"
                          data-testid={`phase-task-remove-${idx}`}
                          disabled={rows.length === 1}
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button variant="secondary" size="sm" onClick={addRow} data-testid="phase-task-add-row">
              <Plus size={14} /> Tambah Baris
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
